import {
  Text as BaseText,
  TextProps as ChakraTextProps,
} from "@chakra-ui/react";
import {
  ForwardRefExoticComponent,
  forwardRef,
  RefAttributes,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import Box from "@ui-kit/layout/Box/Box.web";
import { useColorMode } from "@shared/hooks/useColorMode";
import { colorModes } from "@shared/theme/props/colors";
import type { TextSharedProps, TextPropsToOmit } from "./Text.props";

const SHIMMER_GRADIENTS = {
  light: `linear-gradient(to right, transparent 0%, ${colorModes.shimmer.highlight.light} 50%, transparent 100%)`,
  dark: `linear-gradient(to right, transparent 0%, ${colorModes.shimmer.highlight.dark} 50%, transparent 100%)`,
} as const;

export type TextProps = TextSharedProps &
  Omit<ChakraTextProps, TextPropsToOmit>;

export type TextExpandableProps = Omit<TextProps, "noOfLines" | "lineClamp"> & {
  maxLines?: number;
  showMoreLabel?: string;
  showLessLabel?: string;
};

type TextComponent = ForwardRefExoticComponent<
  TextProps & RefAttributes<HTMLParagraphElement>
> & {
  Strong: (props: TextProps) => JSX.Element;
  Upper: (props: TextProps) => JSX.Element;
  UpperBold: (props: TextProps) => JSX.Element;
  Shimmer: (props: TextProps & { duration?: string }) => JSX.Element;
  Expandable: (props: TextExpandableProps) => JSX.Element;
};

const Text = forwardRef<HTMLParagraphElement, TextProps>(function Text(
  {
    noOfLines,
    lineClamp,
    isTruncated,
    truncate,
    align,
    textAlign,
    size,
    fontSize,
    sx,
    css,
    ...rest
  },
  ref
) {
  const lineClampValue = lineClamp !== undefined ? lineClamp : noOfLines;
  const truncateValue = truncate !== undefined ? truncate : isTruncated;
  const textAlignValue = textAlign !== undefined ? textAlign : align;
  const fontSizeValue = fontSize !== undefined ? fontSize : size;

  const mergedCss =
    sx && css ? ([css, sx] as unknown as ChakraTextProps["css"]) : sx ?? css;

  return (
    <BaseText
      ref={ref}
      lineClamp={lineClampValue}
      truncate={truncateValue}
      textAlign={textAlignValue}
      fontSize={fontSizeValue}
      css={mergedCss}
      {...rest}
    />
  );
}) as TextComponent;

export default Text;

Text.Strong = function Strong(props: TextProps): JSX.Element {
  return <Text color="fg" fontWeight="semibold" {...props} />;
};

Text.Upper = function Upper(props: TextProps): JSX.Element {
  return <Text textTransform="uppercase" {...props} />;
};

Text.UpperBold = function UpperBold(props: TextProps): JSX.Element {
  return <Text.Upper fontWeight="bold" {...props} />;
};

Text.Shimmer = function Shimmer({
  children,
  duration = "2s",
  ...props
}: TextProps & { duration?: string }): JSX.Element {
  const { colorMode } = useColorMode();
  const shimmerGradient =
    SHIMMER_GRADIENTS[colorMode] ?? SHIMMER_GRADIENTS.light;

  return (
    <Text
      position="relative"
      display="inline-block"
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        inset: 0,
        backgroundImage: shimmerGradient,
        transform: "translateX(-100%)",
        animation: `shimmer ${duration} linear infinite`,
        pointerEvents: "none",
        zIndex: 1,
      }}
      {...props}
    >
      {children}
    </Text>
  );
};

Text.Expandable = function Expandable({
  children,
  maxLines = 5,
  showMoreLabel = "Show more",
  showLessLabel = "Show less",
  ...props
}: TextExpandableProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (element && !isExpanded) {
      setIsTruncated(element.scrollHeight > element.clientHeight);
    }
  }, [children, isExpanded]);

  return (
    <Box>
      <Text
        ref={textRef}
        noOfLines={isExpanded ? undefined : maxLines}
        {...props}
      >
        {children}
      </Text>
      {(isTruncated || isExpanded) && (
        <Text
          color="fg.muted"
          textDecoration="underline"
          cursor="pointer"
          onClick={() => setIsExpanded(!isExpanded)}
          mt="2xs"
          {...props}
        >
          {isExpanded ? showLessLabel : showMoreLabel}
        </Text>
      )}
    </Box>
  );
};
